/**
 * Which palette the page wears.
 *
 * Three preferences, two palettes. `auto` is not a third set of colours — it is
 * a standing instruction to follow the browser, resolved to a concrete `light`
 * or `dark` and re-resolved whenever the system flips. That is what keeps
 * `index.css` to one definition per palette: the stylesheet only ever sees
 * `data-theme="light"` or `data-theme="dark"`, never the preference itself.
 *
 * The same resolution runs inline in `index.html` before the bundle loads. The
 * duplication is deliberate and small: a theme applied after first paint is a
 * flash of the wrong palette, and on a page whose whole design is warm-black,
 * a white flash is the most visible bug on the site.
 */

export type ThemePreference = 'auto' | 'light' | 'dark'
export type Theme = 'light' | 'dark'

/** Shared with the inline script in `index.html`. Changing it orphans the
 *  stored preference of everyone who has ever set one, so don't. */
export const THEME_KEY = 'blackbeard.theme'

const QUERY = '(prefers-color-scheme: light)'

function isPreference(value: unknown): value is ThemePreference {
  return value === 'auto' || value === 'light' || value === 'dark'
}

/** What the browser is asking for right now. */
export function systemTheme(): Theme {
  return window.matchMedia(QUERY).matches ? 'light' : 'dark'
}

/**
 * The stored preference, defaulting to `auto`.
 *
 * Storage throws rather than returning null in a browser set to block site
 * data, so every read and write here is guarded: a reader with cookies off gets
 * the browser's own choice and a toggle that works for the session, which is a
 * better answer than a blank page.
 */
export function storedPreference(): ThemePreference {
  try {
    const raw = window.localStorage.getItem(THEME_KEY)
    if (isPreference(raw)) return raw
  } catch {
    /* blocked; auto is the right default anyway */
  }
  return 'auto'
}

/** Resolve a preference to the palette it means at this moment. */
export function resolve(preference: ThemePreference): Theme {
  return preference === 'auto' ? systemTheme() : preference
}

/** Put a preference into effect, and remember it. */
export function applyPreference(preference: ThemePreference): void {
  const theme = resolve(preference)
  document.documentElement.setAttribute('data-theme', theme)
  // The browser paints its own chrome — the address bar on mobile, the
  // scrollbar gutter — from this rather than from the stylesheet, so a page
  // that switches without it keeps a strip of the other theme at the edge.
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', theme === 'light' ? '#fbf8f2' : '#0d0b09')

  try {
    // `auto` is stored explicitly rather than by removing the key: "I have not
    // chosen" and "I chose to follow the browser" are the same behaviour today
    // and would stop being if a future default were anything but auto.
    window.localStorage.setItem(THEME_KEY, preference)
  } catch {
    /* the preference still applies for this session */
  }
}

/**
 * Follow the system while the preference is `auto`.
 *
 * Returns its own teardown. Without this, a reader on `auto` whose machine
 * turns dark at sunset keeps the daylight palette until they reload — which
 * looks like the toggle not working rather than like a missing listener.
 */
export function watchSystem(onChange: () => void): () => void {
  const media = window.matchMedia(QUERY)
  media.addEventListener('change', onChange)
  return () => media.removeEventListener('change', onChange)
}
